'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Image from 'next/image'
import { useRouter } from 'next/navigation'
import { X, Minus, Plus, Trash2, Check, Gift } from 'lucide-react'
import { useCart, type CartLine } from '@/lib/cart'
import { useCartAvailability, explainQtyAcrossPools } from '@/lib/useCartAvailability'
import { formatPrice, asThumbVariantIfPossible, computeShipping, shipsBeyondContinentalUs, SET_SHIPPING_RATE, FREE_WING_SET_PROMO_CODE } from '@sifly/shared-utils'
import type { ShippingInput, FulfillmentOptions } from '@sifly/shared-utils'
import { trackMetaEvent } from '@/lib/metaPixel'
import type { CheckoutCartItem, ManualPaymentMethod, ProductSource } from '@sifly/shared-types'
import { ImagePlaceholder } from '@sifly/shared-ui/product'
import ManualMethodList, { type ManualMethodSelection } from './checkout/ManualMethodList'
import CustomerDetailsForm from './checkout/CustomerDetailsForm'
import FulfillmentChoice from './checkout/FulfillmentChoice'
import BundleSpecList from './checkout/BundleSpecList'
import type { PublicPickupLocation } from '@/lib/cartFulfillment'
import type { ManualCustomerDetails } from '@/lib/manualCheckout'

/** Ready-to-add accessory suggestion from /api/cart-cross-sells. */
type CartCrossSellItem = {
  productId: string
  name: string
  slug: string | null
  photo: string | null
  source: ProductSource
  productType: 'simple'
  skuId: string
  skuName: string
  unitPrice: number
  upsellNote: string | null
}

/** A Wing Set the customer may claim free under the Labor Day promo. */
type FreeWingSetOption = {
  productId: string
  name: string
  slug: string | null
  photo: string | null
  source: ProductSource
  productType: 'simple'
  skuId: string
  skuName: string
  listPrice: number
}

/**
 * How this cart intends to pay. Two shapes, because they go to two different
 * places: `stripe` hands off to Stripe Checkout (`/api/checkout`), `manual`
 * creates the order here and sends the customer to its pay page
 * (`/api/checkout/manual`). Keeping them in ONE state means there is exactly
 * one selected payment method at any moment, rather than two booleans that can
 * both be true.
 */
type CartPayChoice =
  | { kind: 'stripe'; payMethod: 'card' | 'bank' }
  | { kind: 'manual'; method: ManualPaymentMethod; otherId?: string }

/**
 * The cart, in the wire shape BOTH checkout routes accept. One builder, so the
 * Stripe path and the manual path can never disagree about what was in the
 * cart. Every price-bearing field is deliberately absent — each route
 * re-resolves the catalog server-side; this carries identity and quantity only.
 */
function toCheckoutItems(items: CartLine[]): CheckoutCartItem[] {
  return items.map(item => {
    const base: CheckoutCartItem = {
      source: item.source,
      productType: item.productType,
      quantity: item.quantity,
      // A HINT. /api/checkout re-derives the entitlement and refuses the
      // session if this cart didn't earn it.
      ...(item.isGift ? { isGift: true } : {}),
    }

    if (item.productType === 'bundle') {
      if (item.source === 'sifly') {
        base.bundleProductId = item.productId
      } else {
        base.dealerBundleProductId = item.productId
      }
      base.optionIds = item.bundleOptionIds
    } else {
      if (item.source === 'sifly') {
        base.skuId = item.skuId ?? undefined
      } else {
        base.dealerSkuId = item.skuId ?? undefined
      }
    }

    return base
  })
}

export function CartDrawer() {
  const { tenantId, freeShippingAch, flatAccessoryRate: layoutAccessoryRate, hasPickupLocations, paymentMethods, checkoutMode, items, addItem, removeItem, updateQuantity, clearCart, subtotal, giftAllowance, giftClaimed, isDrawerOpen, closeDrawer } = useCart()
  const router = useRouter()
  // Under the embedded flag the drawer is cart-only: delivery, pay method and
  // the customer's details all live on /checkout (greenfield checkout phase
  // 2). Hosted mode keeps the pre-phase-2 drawer untouched.
  const embedded = checkoutMode === 'embedded'
  const [checkingOut, setCheckingOut] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Pay-method choice. Card is the default; the Bank-transfer perk only appears
  // when this cart holds an eFoil set AND the dealer enabled free-ACH shipping
  // (the $300 saving makes the extra ACH step worth surfacing — for an
  // accessory-only cart it isn't). The server re-derives the discount from the
  // dealer toggle and the actual payment method, so this is a UX hint, not the
  // source of truth.
  //
  // A manual method (wire / Zelle / check / a dealer's own) is a THIRD kind of
  // choice, not a third value of this one: it doesn't go to Stripe at all. So
  // the state is a union, and `payMethod` below is derived from it — every
  // existing reader keeps seeing exactly the 'card' | 'bank' it always saw.
  const [payChoice, setPayChoice] = useState<CartPayChoice>({ kind: 'stripe', payMethod: 'card' })

  // Pickup / Shipping. Only asked for when the tenant has a pickup location —
  // otherwise the drawer is exactly what it was. The server says which cards
  // this cart may use; the checkout routes re-check.
  const [fulfillmentData, setFulfillmentData] = useState<{ options: FulfillmentOptions; locations: PublicPickupLocation[] } | null>(null)
  const [fulfillmentMethod, setFulfillmentMethod] = useState<'ship' | 'pickup'>('ship')
  const [pickupLocationId, setPickupLocationId] = useState<string | null>(null)
  // Quantity never changes which methods a cart allows, so it isn't part of the key.
  const fulfillmentKey = useMemo(
    () => JSON.stringify(toCheckoutItems(items).map(({ quantity: _q, ...rest }) => rest)),
    [items],
  )
  useEffect(() => {
    if (!isDrawerOpen || !hasPickupLocations || items.length === 0 || embedded) return
    let cancelled = false
    fetch('/api/checkout/fulfillment-options', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, items: toCheckoutItems(items) }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { options: FulfillmentOptions; locations: PublicPickupLocation[] } | null) => {
        if (cancelled || !d?.options) return
        setFulfillmentData(d)
        // Shipping stays the default; pickup is preselected only when shipping
        // isn't possible for this cart.
        if (!d.options.ship.available && d.options.pickup.available) setFulfillmentMethod('pickup')
        if (d.options.ship.available && !d.options.pickup.available) setFulfillmentMethod('ship')
        setPickupLocationId((prev) => (d.locations.some((l) => l.id === prev) ? prev : d.locations[0]?.id ?? null))
      })
      .catch(() => {
        // Calm fallback: no cards, today's shipping-only drawer. Checkout still
        // validates server-side.
        if (!cancelled) setFulfillmentData(null)
      })
    return () => {
      cancelled = true
    }
    // Keyed on fulfillmentKey, not item identity: a quantity change must not refetch.
  }, [isDrawerOpen, hasPickupLocations, fulfillmentKey, tenantId, embedded])
  const isPickup = fulfillmentMethod === 'pickup' && !!fulfillmentData?.options.pickup.available
  const fulfillmentBlockedReason = fulfillmentData?.options.blockedReason ?? null
  const isStripe = payChoice.kind === 'stripe'
  // 'card' for a manual choice on purpose: a manual method is neither
  // `free_shipping_ach` nor `payMethod === 'bank'`, so it pays standard
  // freight — matching /api/checkout/manual, which passes
  // `freeShippingApplied: false` unconditionally. Disagreeing here would show
  // the customer a total the server would not charge.
  const payMethod: 'card' | 'bank' = isStripe ? payChoice.payMethod : 'card'
  const hasSet = useMemo(() => items.some((i) => i.productType === 'bundle'), [items])
  // Free-ACH shipping only prices shipping, so it disappears for pickup (the
  // effect below then falls payment back to card).
  const offerAch = freeShippingAch && hasSet && !isPickup
  useEffect(() => {
    // Same reset as before — but only within the Stripe branch. Losing the ACH
    // perk (last set removed) must not silently drag a customer who picked
    // Zelle back onto card.
    if (offerAch) return
    setPayChoice((prev) =>
      prev.kind === 'stripe' && prev.payMethod !== 'card' ? { kind: 'stripe', payMethod: 'card' } : prev,
    )
  }, [offerAch])

  // Manual-checkout form state. Held here, not in the form, so it survives the
  // form unmounting when the customer flips back to card and returns.
  const [customer, setCustomer] = useState<Partial<ManualCustomerDetails>>({})

  // ONE idempotency key per checkout ATTEMPT.
  //
  // Lifetime, and why a ref rather than state or a constant: /api/checkout/manual
  // derives the order's dedupe ref from sha256(tenant + this key + email), so a
  // per-mount or per-browser constant would pin one order per (tenant, email)
  // forever — the customer's SECOND purchase would silently resolve to the
  // first order's pay page. Minted lazily on the first submit and reused by
  // every retry of that submit (a network failure, a 500, a corrected cart) —
  // which is the entire point: the retry must land on the order the first POST
  // may already have created. Cleared on a 200, so the next checkout is a new
  // attempt with a new key. A ref, not state, because changing it must not
  // re-render, and reading it must never lag a render.
  const idempotencyKeyRef = useRef<string | null>(null)

  // The store's accessory rate arrives with the (edge-cached) layout, so it can
  // trail a dealer's edit. When the manual route answers `shipping_changed` it
  // names the live rate; the preview adopts it so the buyer sees — and then
  // confirms — the figure they will actually be charged.
  const [liveAccessoryRate, setLiveAccessoryRate] = useState<{ rate: number | undefined } | null>(null)
  const flatAccessoryRate = liveAccessoryRate ? liveAccessoryRate.rate : layoutAccessoryRate

  // Shipping preview via the shared module (display-only — the server recharges
  // from products.default_shipping at checkout). Sets carry SET_SHIPPING_RATE;
  // accessories ride free when a set is present, else the STORE's flat accessory
  // rate (undefined → SiFly's default), the same figure the server charges.
  const shippingInputs: ShippingInput[] = useMemo(
    () =>
      items.map((i, idx) => ({
        id: String(idx),
        kind: i.productType === 'bundle' ? 'set' : 'accessory',
        unitShipping: i.productType === 'bundle' ? SET_SHIPPING_RATE : 0,
        quantity: i.quantity,
      })),
    [items],
  )
  // What shipping costs without the ACH waiver — used for the perk's "save $X"
  // and the Card option's standard-rate label.
  const standardShipping = useMemo(() => computeShipping(shippingInputs, { flatAccessoryRate }).charged, [shippingInputs, flatAccessoryRate])
  // The drawer's manual form already holds the address, so the preview applies
  // the same continental-US rule the server applies: showing freight here and
  // $0 on the order the buyer lands on is the drawer disagreeing with itself.
  const manualShipTo = customer.shipping_address
  const beyondContinentalUs =
    !isPickup && !!manualShipTo?.country && !!manualShipTo?.state &&
    shipsBeyondContinentalUs({ country: manualShipTo.country, state: manualShipTo.state })
  const shipping = useMemo(
    () => computeShipping(shippingInputs, { freeShippingApplied: offerAch && payMethod === 'bank', pickup: isPickup, beyondContinentalUs, flatAccessoryRate }),
    [shippingInputs, offerAch, payMethod, isPickup, beyondContinentalUs, flatAccessoryRate],
  )
  const orderTotal = subtotal + shipping.charged

  // Post-add cross-sell suggestions ("Add to your kit"). Fetched when the
  // drawer opens with items, cached on the sorted cart product-id set so a
  // reopen with the same cart doesn't refetch. Render-nothing on empty/error.
  const [suggestions, setSuggestions] = useState<CartCrossSellItem[]>([])
  const fetchedKeyRef = useRef<string | null>(null)
  const cartKey = useMemo(
    () => [...new Set(items.map((i) => i.productId))].sort().join(','),
    [items],
  )

  useEffect(() => {
    if (!isDrawerOpen || items.length === 0) return
    if (fetchedKeyRef.current === cartKey) return
    const productIds = [...new Set(items.map((i) => i.productId))]
    let cancelled = false
    fetch('/api/cart-cross-sells', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productIds, tenantId }),
    })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        if (cancelled) return
        setSuggestions((d?.items ?? []) as CartCrossSellItem[])
        fetchedKeyRef.current = cartKey
      })
      .catch(() => {
        if (!cancelled) setSuggestions([])
      })
    return () => {
      cancelled = true
    }
  }, [isDrawerOpen, cartKey, items, tenantId])

  // Labor Day promo. Fetched once per drawer session while the cart still has
  // an unclaimed bonus; the route answers empty outside the promo window, so no
  // date logic lives here. `remaining` drives both the heading and whether the
  // panel shows at all — the cart context is the one place the allowance is
  // computed (and the one place an unearned gift is removed again).
  const remainingGifts = Math.max(0, giftAllowance - giftClaimed)
  const [wingSetOptions, setWingSetOptions] = useState<FreeWingSetOption[]>([])
  const wingSetsFetchedRef = useRef(false)

  useEffect(() => {
    if (!isDrawerOpen || remainingGifts === 0) return
    if (wingSetsFetchedRef.current) return
    wingSetsFetchedRef.current = true
    let cancelled = false
    fetch('/api/free-wing-sets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId }),
    })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        if (!cancelled) setWingSetOptions((d?.items ?? []) as FreeWingSetOption[])
      })
      .catch(() => {
        if (!cancelled) setWingSetOptions([])
      })
    return () => {
      cancelled = true
    }
  }, [isDrawerOpen, remainingGifts, tenantId])

  function handleClaimWingSet(w: FreeWingSetOption) {
    addItem({
      source: w.source,
      productId: w.productId,
      productName: w.name,
      productType: 'simple',
      skuId: w.skuId,
      skuName: w.skuName,
      quantity: 1,
      unitPrice: 0,
      photo: w.photo,
      isGift: true,
      giftListPrice: w.listPrice,
    })
  }

  function handleAddSuggestion(s: CartCrossSellItem) {
    addItem({
      source: s.source,
      productId: s.productId,
      productName: s.name,
      productType: 'simple',
      skuId: s.skuId,
      skuName: s.skuName,
      quantity: 1,
      unitPrice: s.unitPrice,
      photo: s.photo,
    })
    // Optimistically drop it; the cart-key change refetches to reconcile.
    setSuggestions((prev) => prev.filter((x) => x.productId !== s.productId))
  }

  // Refresh availability each time the drawer opens — catches stock changes
  // between add-to-cart and checkout.
  const [openNonce, setOpenNonce] = useState(0)
  useEffect(() => {
    if (isDrawerOpen) setOpenNonce((n) => n + 1)
  }, [isDrawerOpen])
  const { statusBySkuId, rowBySkuId, bundleStatusByLineId } = useCartAvailability(items, tenantId, openNonce)
  const hasOutOfStock = useMemo(
    () =>
      items.some((i) => {
        if (i.productType === 'bundle' && i.bundleOptionIds?.length) {
          return bundleStatusByLineId[i.lineId] === 'out_of_stock'
        }
        return !!i.skuId && statusBySkuId[i.skuId] === 'out_of_stock'
      }),
    [items, statusBySkuId, bundleStatusByLineId],
  )

  if (!isDrawerOpen) return null

  async function handleCheckout() {
    if (items.length === 0) return
    setCheckingOut(true)
    setError(null)

    const checkoutItems = toCheckoutItems(items)

    const res = await fetch('/api/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: checkoutItems,
        tenantId,
        fulfillment: isPickup ? { method: 'pickup', locationId: pickupLocationId } : { method: 'ship' },
        // Only send a pay-method when the perk is on offer — otherwise let the
        // server use Stripe's automatic methods (current behavior).
        ...(offerAch ? { payMethod } : {}),
        successUrl: `${window.location.origin}/checkout/success`,
        cancelUrl: window.location.href,
      }),
    })

    const data = await res.json()
    if (!res.ok || !data.url) {
      setError(data.error ?? 'Checkout failed. Please try again.')
      setCheckingOut(false)
      return
    }
    trackMetaEvent('InitiateCheckout', { content_ids: items.filter(i => !i.isGift).map(i => i.productId), content_type: 'product', value: items.filter(i => !i.isGift).reduce((sum, i) => sum + i.unitPrice * i.quantity, 0), currency: 'USD' })
    clearCart()
    window.location.href = data.url
  }

  /**
   * The manual (non-Stripe) submit. `details` is the VALIDATED, NORMALIZED
   * object `CustomerDetailsForm` hands us — never the `customer` state, which
   * still holds exactly what was typed. The route folds the normalized email
   * into its dedupe key, so submitting the raw one would derive a different
   * key and mint a second order for what is one attempt.
   */
  async function handleManualCheckout(details: ManualCustomerDetails) {
    if (items.length === 0 || payChoice.kind !== 'manual') return
    setCheckingOut(true)
    setError(null)

    idempotencyKeyRef.current ??= globalThis.crypto?.randomUUID?.() ?? null
    const idempotencyKey = idempotencyKeyRef.current
    if (!idempotencyKey) {
      // The route requires a uuid-shaped key and refuses anything else, so a
      // browser without crypto.randomUUID cannot use this path. Say so rather
      // than POST something that will come back a confusing 400.
      setError('This browser could not start a secure checkout. Please pay by card instead.')
      setCheckingOut(false)
      return
    }

    const res = await fetch('/api/checkout/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: toCheckoutItems(items),
        tenantId,
        fulfillment: isPickup ? { method: 'pickup', locationId: pickupLocationId } : { method: 'ship' },
        method: payChoice.method,
        otherId: payChoice.otherId ?? null,
        idempotencyKey,
        customer: details,
        // What the buyer is looking at. The server prices shipping itself and
        // refuses (writing nothing) if this differs — never a price input.
        expectedShippingCents: Math.round(shipping.charged * 100),
      }),
      // A network failure is precisely what the idempotency key exists for —
      // the POST may have created the order. Keep the key and let them retry,
      // rather than throwing and leaving the button stuck on "Placing order".
    }).catch(() => null)

    if (!res) {
      setError('Could not reach the store. Check your connection and try again.')
      setCheckingOut(false)
      return
    }

    const data = (await res.json().catch(() => ({}))) as { payUrl?: string; error?: string; code?: string; flatAccessoryRate?: number | null }
    if (data.code === 'shipping_changed') {
      setLiveAccessoryRate({ rate: typeof data.flatAccessoryRate === 'number' ? data.flatAccessoryRate : undefined })
    }
    if (!res.ok || !data.payUrl) {
      setError(data.error ?? 'Checkout failed. Please try again.')
      setCheckingOut(false)
      return
    }

    // This attempt is done. Discard the key so a later checkout by the same
    // customer derives a different ref and gets its own order.
    idempotencyKeyRef.current = null
    trackMetaEvent('InitiateCheckout', { content_ids: items.filter(i => !i.isGift).map(i => i.productId), content_type: 'product', value: items.filter(i => !i.isGift).reduce((sum, i) => sum + i.unitPrice * i.quantity, 0), currency: 'USD' })
    clearCart()
    window.location.href = data.payUrl
  }

  function chooseManual(selection: ManualMethodSelection) {
    setError(null)
    setPayChoice({ kind: 'manual', method: selection.method, otherId: selection.otherId })
  }

  function chooseCard() {
    setError(null)
    setPayChoice({ kind: 'stripe', payMethod: 'card' })
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        role="presentation"
        onClick={closeDrawer}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-full max-w-md bg-[var(--color-bg-primary)] shadow-xl z-50 flex flex-col animate-slideInRight">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--color-border)]">
          <h2 className="font-semibold text-[var(--color-text)] text-lg">Cart ({items.length})</h2>
          <button
            onClick={closeDrawer}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
            aria-label="Close cart"
          >
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p className="text-[var(--color-text-muted)] text-sm">Your cart is empty</p>
              <button
                onClick={closeDrawer}
                className="mt-3 text-sm text-[var(--color-cta-readable)] hover:underline"
              >
                Continue shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => {
                const isBundle = item.productType === 'bundle' && !!item.bundleOptionIds?.length
                const lineOut = isBundle
                  ? bundleStatusByLineId[item.lineId] === 'out_of_stock'
                  : !!item.skuId && statusBySkuId[item.skuId] === 'out_of_stock'
                const availRow = !isBundle && item.skuId ? rowBySkuId[item.skuId] : undefined
                let overflow: { local: number; usa: number; bulgaria: number } | null = null
                if (!lineOut && availRow && item.quantity > Math.max(0, availRow.dealer_local_qty)) {
                  const { local, usa, bulgaria } = explainQtyAcrossPools(item.quantity, availRow)
                  if (usa + bulgaria > 0) overflow = { local, usa, bulgaria }
                }
                return (
                <div
                  key={item.lineId}
                  className={`flex gap-3 ${lineOut ? 'bg-[var(--color-error-surface)] -mx-2 px-2 py-2 rounded-lg' : ''}`}
                >
                  {/* Thumbnail */}
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-[var(--color-surface-muted)] flex-shrink-0 relative">
                    {item.photo ? (
                      <Image src={asThumbVariantIfPossible(item.photo) ?? item.photo} alt={item.productName} fill sizes="64px" className="object-contain" />
                    ) : (
                      <ImagePlaceholder label={item.productName} className="w-full h-full" />
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text)] truncate">{item.productName}</p>
                    {/* Simple products: the variant name. (For a bundle, skuName is
                        just a 60-char truncation of the breakdown — see below.) */}
                    {item.productType !== 'bundle' && (
                      <p className="text-xs text-[var(--color-text-secondary)] truncate">{item.skuName}</p>
                    )}
                    {item.productType === 'bundle' && <BundleSpecList item={item} />}
                    {lineOut && (
                      <p className="mt-1 text-xs text-[var(--color-error-strong)]">
                        Just sold out —{' '}
                        <button
                          type="button"
                          onClick={() => removeItem(item.lineId)}
                          className="underline font-medium hover:opacity-80"
                        >
                          remove from cart
                        </button>
                      </p>
                    )}
                    {overflow && (
                      <p className="mt-1 text-xs text-[var(--color-warning-strong)]">
                        {overflow.local > 0 && `${overflow.local} ship in 1–2 days`}
                        {overflow.local > 0 && overflow.usa > 0 && ', '}
                        {overflow.usa > 0 && `${overflow.usa} ship in 2–3 days`}
                        {(overflow.local > 0 || overflow.usa > 0) && overflow.bulgaria > 0 && ', '}
                        {overflow.bulgaria > 0 && `${overflow.bulgaria} ship in 3–5 weeks`}
                      </p>
                    )}

                    <div className="flex items-center justify-between mt-2">
                      {/* Quantity — a bonus is one unit, so it gets a static
                          label instead of a stepper the server would reject. */}
                      {item.isGift ? (
                        <span className="inline-flex items-center gap-1 text-[11px] font-medium tracking-wide uppercase text-[var(--color-text-muted)]">
                          <Gift size={12} aria-hidden />
                          {FREE_WING_SET_PROMO_CODE}
                        </span>
                      ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateQuantity(item.lineId, item.quantity - 1)}
                            className="w-6 h-6 rounded border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
                          >
                            <Minus size={12} />
                          </button>
                          <span className="text-sm text-[var(--color-text)] w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.lineId, item.quantity + 1)}
                            className="w-6 h-6 rounded border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      )}

                      {/* Price + remove */}
                      <div className="flex items-center gap-2">
                        {item.isGift ? (
                          <span className="text-sm font-medium text-[var(--color-text)]">
                            {item.giftListPrice ? (
                              <span className="mr-1.5 font-normal text-[var(--color-text-muted)] line-through">
                                {formatPrice(item.giftListPrice)}
                              </span>
                            ) : null}
                            Free
                          </span>
                        ) : (
                          <span className="text-sm font-medium text-[var(--color-text)]">
                            {formatPrice(item.unitPrice * item.quantity)}
                          </span>
                        )}
                        <button
                          onClick={() => removeItem(item.lineId)}
                          className="text-[var(--color-text-muted)] hover:opacity-80 transition-colors"
                          aria-label="Remove item"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                )
              })}

              {/* Labor Day promo — the free Wing Set picker. Sits ABOVE the paid
                  kit suggestions: it costs nothing and is the reason the
                  customer may have come, so it must not read as another upsell.
                  Same panel shape as "Add to your kit" for consistency, marked
                  apart by the gift icon and a stronger heading — NOT by the
                  accent: the drawer's accent budget belongs to actions, and a
                  section label isn't one (apps/storefront/__tests__/
                  accentBudget.test.ts pins this). */}
              {remainingGifts > 0 && wingSetOptions.length > 0 && (
                <div className="mt-4 rounded-xl bg-[var(--color-surface-muted)] ring-1 ring-[var(--color-border)] px-4 py-3">
                  <p className="flex items-center gap-1.5 text-xs font-medium text-[var(--color-text)]">
                    <Gift size={13} aria-hidden />
                    {remainingGifts > 1
                      ? `Choose ${remainingGifts} free Wing Sets`
                      : 'Choose your free Wing Set'}
                  </p>
                  <p className="mt-0.5 text-[11px] leading-snug text-[var(--color-text-muted)]">
                    Included with your eFoil set — code {FREE_WING_SET_PROMO_CODE} applied. Offer ends
                    Tuesday, September 8.
                  </p>
                  <div className="mt-1 divide-y divide-[var(--color-border)]">
                    {wingSetOptions.map((w) => (
                      <div key={w.productId} className="flex items-center gap-3 py-3 last:pb-0">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-[var(--color-surface-muted)] flex-shrink-0 relative">
                          {w.photo ? (
                            <Image
                              src={asThumbVariantIfPossible(w.photo) ?? w.photo}
                              alt={w.name}
                              fill
                              sizes="40px"
                              className="object-contain"
                            />
                          ) : (
                            <ImagePlaceholder label={w.name} className="w-full h-full" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[var(--color-text)] truncate">{w.name}</p>
                          <p className="text-xs text-[var(--color-text-secondary)]">
                            <span className="line-through text-[var(--color-text-muted)]">
                              {formatPrice(w.listPrice)}
                            </span>{' '}
                            <span className="font-medium text-[var(--color-text)]">Free</span>
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleClaimWingSet(w)}
                          className="text-xs font-medium text-[var(--color-text)] bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg px-3 py-1.5 hover:bg-[var(--color-surface-muted)] transition-colors"
                        >
                          Choose
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Post-add cross-sell — "Add to your kit" accessories. Wrapped in a
                  shaded panel so it reads as a distinct "optional extras" zone, not
                  as items already in the cart (calm neutral surface token —
                  emerald is reserved for selected states). */}
              {suggestions.length > 0 && (
                <div className="mt-4 rounded-xl bg-[var(--color-surface-muted)] ring-1 ring-[var(--color-border)] px-4 py-3">
                  <p className="text-xs font-medium text-[var(--color-text-secondary)] mb-1">Add to your kit</p>
                  <div className="divide-y divide-[var(--color-border)]">
                    {suggestions.map((s) => (
                      <div key={s.productId} className="flex items-center gap-3 py-3 first:pt-1 last:pb-0">
                        <div className="w-10 h-10 rounded-md overflow-hidden bg-[var(--color-surface-muted)] flex-shrink-0 relative">
                          {s.photo ? (
                            <Image
                              src={asThumbVariantIfPossible(s.photo) ?? s.photo}
                              alt={s.name}
                              fill
                              sizes="40px"
                              className="object-contain"
                            />
                          ) : (
                            <ImagePlaceholder label={s.name} className="w-full h-full" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[var(--color-text)] truncate">{s.name}</p>
                          <p className="text-xs text-[var(--color-text-secondary)]">{formatPrice(s.unitPrice)}</p>
                          {s.upsellNote && (
                            <p className="text-[11px] leading-snug text-[var(--color-text-muted)] mt-0.5">{s.upsellNote}</p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddSuggestion(s)}
                          className="text-xs font-medium text-[var(--color-text)] bg-[var(--color-bg-primary)] border border-[var(--color-border)] rounded-lg px-3 py-1.5 hover:bg-[var(--color-surface-muted)] transition-colors"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-4 border-t border-[var(--color-border)] space-y-3">
            {embedded ? (
              <>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[var(--color-text-secondary)]">Subtotal</span>
                    <span className="text-lg font-semibold text-[var(--color-text)]">{formatPrice(subtotal)}</span>
                  </div>
                  <p className="text-[11px] text-[var(--color-text-muted)]">Shipping and taxes calculated at checkout.</p>
                </div>
                {hasOutOfStock && (
                  <p className="text-xs text-[var(--color-error-strong)] bg-[var(--color-error-surface)] rounded-md px-3 py-2">
                    Some items just sold out. Remove them to continue to checkout.
                  </p>
                )}
                <button
                  type="button"
                  onClick={() => { closeDrawer(); router.push('/checkout') }}
                  disabled={hasOutOfStock}
                  aria-disabled={hasOutOfStock}
                  className="cta-primary w-full py-3 rounded-xl font-medium disabled:opacity-50 transition-opacity"
                >
                  {hasOutOfStock ? 'Remove sold-out items' : 'Continue to checkout'}
                </button>
              </>
            ) : (
            <>
            {fulfillmentData && (
              <FulfillmentChoice
                options={fulfillmentData.options}
                locations={fulfillmentData.locations}
                method={isPickup ? 'pickup' : 'ship'}
                locationId={pickupLocationId}
                shippingLabel={standardShipping > 0 ? formatPrice(standardShipping) : 'Free'}
                onMethodChange={(m) => { setError(null); setFulfillmentMethod(m) }}
                onLocationChange={setPickupLocationId}
              />
            )}
            {fulfillmentBlockedReason && (
              <p className="text-xs text-[var(--color-text-secondary)] bg-[var(--color-surface-muted)] rounded-md px-3 py-2">
                {fulfillmentBlockedReason}
              </p>
            )}
            {/* Pay-method choice — only when this cart can earn the free-shipping
                perk (a set in cart + dealer toggle on). Emerald = selected. */}
            {offerAch && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-[var(--color-text-secondary)]">Payment method</p>
                <button
                  type="button"
                  onClick={() => { setError(null); setPayChoice({ kind: 'stripe', payMethod: 'bank' }) }}
                  aria-pressed={isStripe && payMethod === 'bank'}
                  className={`w-full flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                    isStripe && payMethod === 'bank' ? 'border-[var(--color-selected)] bg-[var(--color-selected-soft)]' : 'border-[var(--color-border)] hover:bg-[var(--color-surface-muted)]'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-[var(--color-text)]">Bank transfer (ACH)</span>
                    <span className="block text-xs font-semibold text-[var(--color-success-strong)]">
                      Free shipping — save {formatPrice(standardShipping)}
                    </span>
                    <span className="block text-xs text-[var(--color-text-secondary)] mt-0.5">
                      Pay from your bank account — we cover the shipping. Clears in a few business days.
                    </span>
                  </span>
                  {isStripe && payMethod === 'bank' && <Check size={16} className="text-[var(--color-selected-readable)] mt-0.5 flex-shrink-0" />}
                </button>
                <button
                  type="button"
                  onClick={chooseCard}
                  aria-pressed={isStripe && payMethod === 'card'}
                  className={`w-full flex items-start justify-between gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors ${
                    isStripe && payMethod === 'card' ? 'border-[var(--color-selected)] bg-[var(--color-selected-soft)]' : 'border-[var(--color-border)] hover:bg-[var(--color-surface-muted)]'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-[var(--color-text)]">Card</span>
                    <span className="block text-xs text-[var(--color-text-secondary)]">
                      Standard shipping ({formatPrice(standardShipping)})
                    </span>
                  </span>
                  {isStripe && payMethod === 'card' && <Check size={16} className="text-[var(--color-selected-readable)] mt-0.5 flex-shrink-0" />}
                </button>
              </div>
            )}

            {/* The dealer's non-Stripe methods. Renders nothing at all — no
                node, no wrapper — unless the dealer enabled one for the
                storefront, so a dealer who hasn't gets a byte-identical cart.
                (Verified against a rendered drawer, not assumed: 7 of the 9
                live tenants are in that state as of 2026-09-08.) */}
            <ManualMethodList
              snapshot={paymentMethods}
              selected={payChoice.kind === 'manual' ? { method: payChoice.method, otherId: payChoice.otherId } : null}
              onSelect={chooseManual}
            />

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-text-secondary)]">Subtotal</span>
                <span className="text-sm text-[var(--color-text)]">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--color-text-secondary)]">{isPickup ? 'Pickup' : 'Shipping'}</span>
                {shipping.reason === 'beyond_continental' ? (
                  <span className="text-sm text-[var(--color-text-muted)]">Quoted separately</span>
                ) : shipping.charged === 0 ? (
                  <span className="text-sm font-medium text-[var(--color-success-strong)]">
                    {shipping.reason === 'free_ach' ? 'Free (bank transfer)' : 'Free'}
                  </span>
                ) : (
                  <span className="text-sm text-[var(--color-text)]">{formatPrice(shipping.charged)}</span>
                )}
              </div>
              {shipping.reason === 'beyond_continental' && (
                <p className="text-[11px] text-[var(--color-text-muted)] -mt-1">
                  Our shipping rates cover the continental US. We&apos;ll email you the shipping cost for your address and collect it before your order ships.
                </p>
              )}
              <p className="text-[11px] text-[var(--color-text-muted)] -mt-1">Taxes calculated at checkout.</p>
              <div className="flex items-center justify-between pt-1.5 border-t border-[var(--color-border)]">
                <span className="text-sm font-medium text-[var(--color-text)]">Total</span>
                <span className="text-lg font-semibold text-[var(--color-text)]">{formatPrice(orderTotal)}</span>
              </div>
            </div>
            {hasOutOfStock && (
              <p className="text-xs text-[var(--color-error-strong)] bg-[var(--color-error-surface)] rounded-md px-3 py-2">
                Some items just sold out. Remove them to continue to checkout.
              </p>
            )}
            {/* Stripe-path error. The manual path shows its own copy inside the
                details form — this block's "Try again" re-runs the STRIPE
                submit, which is the wrong request for a manual choice. */}
            {error && isStripe && (
              <div className="text-xs text-[var(--color-error-strong)] bg-[var(--color-error-surface)] border border-[var(--color-error-border)] rounded-md px-3 py-2">
                Checkout could not start. Please try again — or contact us if it keeps happening.{' '}
                <button
                  type="button"
                  onClick={handleCheckout}
                  disabled={checkingOut}
                  className="font-medium underline hover:opacity-80 disabled:opacity-50"
                >
                  Try again
                </button>
              </div>
            )}
            {payChoice.kind === 'manual' && !hasOutOfStock && !fulfillmentBlockedReason ? (
              <>
                <CustomerDetailsForm
                  value={customer}
                  onChange={setCustomer}
                  onSubmit={handleManualCheckout}
                  submitting={checkingOut}
                  error={error}
                  fulfillmentMethod={isPickup ? 'pickup' : 'ship'}
                />
                <button
                  type="button"
                  onClick={chooseCard}
                  className="w-full text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] py-1"
                >
                  Pay by card instead
                </button>
              </>
            ) : (
              <button
                onClick={handleCheckout}
                disabled={checkingOut || hasOutOfStock || !!fulfillmentBlockedReason}
                aria-disabled={checkingOut || hasOutOfStock || !!fulfillmentBlockedReason}
                className="cta-primary w-full py-3 rounded-xl font-medium disabled:opacity-50 transition-opacity"
              >
                {checkingOut ? 'Redirecting...' : hasOutOfStock ? 'Remove sold-out items' : 'Checkout'}
              </button>
            )}
            </>
            )}
            <button
              onClick={closeDrawer}
              className="w-full text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] py-1"
            >
              Continue shopping
            </button>
          </div>
        )}
      </div>
    </>
  )
}
